import React, { useEffect, useState } from 'react';
import './App.css';
import configData from './config/hardware.json';
import translations from './translations';
import { getAuthHeaders, initKeycloakAuth } from './auth/keycloak';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';
const FRONTEND_VERSION = process.env.REACT_APP_FRONTEND_VERSION || '1.0.1';
const TEMPLATE_NAME_DISALLOWED_CHAR_RE = /[^A-Za-z0-9.-]/;
const TEMPLATE_NAME_DISALLOWED_CHARS_RE = /[^A-Za-z0-9.-]/g;
const POLISH_CHAR_REPLACEMENTS = {
  ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z',
  Ą: 'A', Ć: 'C', Ę: 'E', Ł: 'L', Ń: 'N', Ó: 'O', Ś: 'S', Ź: 'Z', Ż: 'Z'
};

const sanitizeTemplateName = (value) => value.replace(TEMPLATE_NAME_DISALLOWED_CHARS_RE, '');
const sanitizeDescription = (value) => value.replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, char => POLISH_CHAR_REPLACEMENTS[char]);

function App() {
  const ENABLE_DOWNLOAD = false; // Set to true to enable automatically download functionality
  const DEFAULT_BRANCH = 'kptesting';

  const [chargerType, setChargerType] = useState(configData.chargers[0].value);
  const [maxPower, setMaxPower] = useState(String(configData.chargers[0]?.defaultPower ?? 360));
  const [currency, setCurrency] = useState(configData.currencies[0].value);
  const [lekoVersion, setLekoVersion] = useState(configData.lekoVersions?.[0]?.value || '');
  const [satellites, setSatellites] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [lastConfigId, setLastConfigId] = useState('');
  const [lastZipName, setLastZipName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [commitAttempted, setCommitAttempted] = useState(false);
  const [loadingSwu, setLoadingSwu] = useState(false);
  const [loadingCommit, setLoadingCommit] = useState(false);
  const [templateAlreadyExists, setTemplateAlreadyExists] = useState(false);
  const [loadingSwuStatus, setLoadingSwuStatus] = useState('');

  const [enableFactoryReset, setEnableFactoryReset] = useState(false);
  const [enableSerialization, setEnableSerialization] = useState(false);
  const [enableEichrecht, setEnableEichrecht] = useState(false);
  const [hasFireExtinguisher, setHasFireExtinguisher] = useState(false);
  const [hasCoolingSystem, setHasCoolingSystem] = useState(false);
  const [serializationMain, setSerializationMain] = useState({ serial: '', model: '', product: '' });
  const [serializationSats, setSerializationSats] = useState({});
  const [mainMeter, setMainMeter] = useState((configData.meters && configData.meters[0]?.value) || 'none');
  const [mainTerminal, setMainTerminal] = useState((configData.terminals && configData.terminals[0]?.value) || 'none');
  const [language, setLanguage] = useState('en');
  const [auth, setAuth] = useState({ loading: true, enabled: false, client: null, error: '' });
  const [backendVersion, setBackendVersion] = useState('unknown');
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);

  useEffect(() => {
    let mounted = true;

    initKeycloakAuth(BACKEND_URL)
      .then(nextAuth => {
        if (mounted) {
          setAuth({ loading: false, error: '', ...nextAuth });
        }
      })
      .catch(error => {
        if (mounted) {
          setAuth({ loading: false, enabled: false, client: null, error: error.message });
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    fetch(`${BACKEND_URL}/api/version`)
      .then(response => response.ok ? response.json() : Promise.reject(new Error('version unavailable')))
      .then(data => {
        if (mounted) {
          setBackendVersion(data.backend || 'unknown');
        }
      })
      .catch(() => {
        if (mounted) {
          setBackendVersion('unknown');
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const t = (key) => {
    return translations[language]?.[key] || key;
  };

  const getImageUrl = (filename) => `${process.env.PUBLIC_URL}/assets/${filename}`;

  const getCurrentUsername = () => (
    auth.client?.tokenParsed?.preferred_username ||
    auth.client?.tokenParsed?.email ||
    'anonymous'
  );

  const getSatelliteImage = (value) => {
    const sat = configData.satellites.find(s => s.value === value);
    return sat ? getImageUrl(sat.image) : getImageUrl('unknown.png');
  };

  const getChargerImage = (value) => {
    const ch = configData.chargers.find(c => c.value === value);
    return ch ? getImageUrl(ch.image) : getImageUrl('unknown.png');
  };

  const findCharger = (value) => configData.chargers.find(c => c.value === value);
  const findConnector = (value) => (configData.connectors || []).find(c => c.value === value);

  const getChargerDefaultPower = (value) => {
    const ch = findCharger(value);
    return (ch && typeof ch.defaultPower === 'number') ? ch.defaultPower : Number(maxPower) || 0;
  };

  const clampChargerPower = (value, chargerValue = chargerType) => {
    const limit = getChargerDefaultPower(chargerValue);
    const num = Number(value);
    if (!Number.isFinite(num)) return '';
    return Math.min(num, limit);
  };

  const clampConnectorPower = (value, connectorType, chargerPowerValue = maxPower) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return 0;
    if (String(connectorType || '').toLowerCase() === AC_TYPE) return num;
    const chargerLimit = Number(chargerPowerValue) || 0;
    return Math.min(num, chargerLimit);
  };

  const getConnectorDefaults = (value, chargerValue = chargerType) => {
    const cn = findConnector(value);
    const connectorPower = (cn && typeof cn.defaultPower === 'number') ? cn.defaultPower : 0;
    const current = (cn && typeof cn.defaultCurrent === 'number') ? cn.defaultCurrent : 0;
    const isAC = String(value || '').toLowerCase() === AC_TYPE;
    const chargerDefaultPower = getChargerDefaultPower(chargerValue);
    return {
      power: isAC ? Math.min(connectorPower, 44) : chargerDefaultPower,
      current: isAC ? 0 : current
    };
  };

  const isAxonEasy = String(chargerType || '').toLowerCase().startsWith('axon_easy_');
  const isAxonEasyDLBS = String(chargerType || '').toLowerCase().startsWith('axon_easy_dlbs_');
  const isAxonEasyLegacy = String(chargerType || '').toLowerCase() === 'axon_easy';
  const axonEasyMeters = (configData.meters || []).filter(
    m => String(m.value || '').toLowerCase() !== 'none'
  );

  const ccsConnectors = connectors.filter(c =>
    ['ccs', 'ccs2'].includes(String(c.type || '').toLowerCase())
  );
  const shouldShowSingleCcsSide =
    isAxonEasy && connectors.length === 1 && ccsConnectors.length === 1;

  const getNextAxonEasyConnectorType = () => {
    const dcCount = connectors.filter(c => ['ccs', 'ccs2'].includes(String(c.type).toLowerCase())).length;
    return dcCount < 2 ? 'ccs' : 'ac';
  };

  const getConnectorImage = (value) => {
    const cn = (configData.connectors || []).find(c => c.value === value);
    return cn ? getImageUrl(cn.image) : getImageUrl('unknown.png');
  };

  const getDefaultOutputs = (value) => {
    const v = String(value || '').toLowerCase().trim();
    return v === 'sat400ccslr' || v.startsWith('sat1500mcs') ? 2 : 1;
  };

  const DC_SET = ['ccs', 'ccs2'];
  const AC_TYPE = 'ac';

  const getDefaultConnector = () => {
    if (isAxonEasy) {
      return getNextAxonEasyConnectorType();
    }
    const list = (configData.connectors || []);
    return list.length ? list[0].value : 'ccs';
  };

  const canAddConnectorOfType = (type, targetIndex = null) => {
    const requestedType = String(type || '').toLowerCase();
    const normalizedConnectors = connectors.map((c, i) => (i === targetIndex ? { ...c, type } : c));
    const dcCount = normalizedConnectors.filter(c => DC_SET.includes(String(c.type).toLowerCase())).length;
    const acCount = normalizedConnectors.filter(c => String(c.type).toLowerCase() === AC_TYPE).length;
    const acIndex = normalizedConnectors.findIndex(c => String(c.type).toLowerCase() === AC_TYPE);

    if (DC_SET.includes(requestedType) && dcCount > 2) return { ok: false, msg: t('errorMaxDcConnectors') };
    if (requestedType === AC_TYPE && acCount > 1) return { ok: false, msg: t('errorMaxAcConnectors') };

    if (isAxonEasy && acCount > 0) {
      if (normalizedConnectors.length < 3) {
        return { ok: false, msg: t('errorAcOnlyWithTwoCcs') };
      }
      if (acIndex !== 2) {
        return { ok: false, msg: t('errorAcOnlyThirdConnector') };
      }
      const firstTwoAreDc = normalizedConnectors.slice(0, 2).every(c =>
        DC_SET.includes(String(c.type).toLowerCase())
      );
      if (!firstTwoAreDc) {
        return { ok: false, msg: t('errorAcOnlyWithTwoCcs') };
      }
    }

    return { ok: true };
  };

  const normalizeAxonEasyConnectorsAfterRemoval = (nextConnectors) => {
    if (!isAxonEasy) return nextConnectors;

    const normalized = [...nextConnectors];
    const dcCount = normalized.filter(c => DC_SET.includes(String(c.type).toLowerCase())).length;
    const acIndex = normalized.findIndex(c => String(c.type).toLowerCase() === AC_TYPE);

    if (acIndex !== -1) {
      const firstTwoAreDc = normalized.slice(0, 2).every(c =>
        DC_SET.includes(String(c.type).toLowerCase())
      );

      if (normalized.length < 3 || acIndex !== 2 || dcCount < 2 || !firstTwoAreDc) {
        return normalized.filter(c => String(c.type).toLowerCase() !== AC_TYPE);
      }
    }

    return normalized;
  };

  const handleAddConnector = () => {
    if (connectors.length >= 3) {
      setErrorMessage(t('errorMaxThreeConnectors'));
      return;
    }
    const def = getDefaultConnector();
    const check = canAddConnectorOfType(def, connectors.length);
    if (!check.ok) {
      setErrorMessage(check.msg);
      return;
    }
    const defaults = getConnectorDefaults(def, chargerType);
    const isSingleCcsType = ['ccs', 'ccs2'].includes(String(def).toLowerCase());
    const newConn = {
      type: def,
      maxPower: clampConnectorPower(defaults.power, def),
      maxCurrent: defaults.current,
      ...(isSingleCcsType ? { side: 'left' } : {})
    };
    setConnectors([...connectors, newConn]);
  };

  const handleRemoveConnector = (idx) => {
    const nextConnectors = connectors.filter((_, i) => i !== idx);
    const normalized = normalizeAxonEasyConnectorsAfterRemoval(nextConnectors);

    if (normalized.length !== nextConnectors.length) {
      setInfoMessage(t('infoRemovedInvalidAcConnector'));
    }

    setConnectors(normalized);
  };

  const handleConnectorChange = (idx, value) => {
    const check = canAddConnectorOfType(value, idx);
    if (!check.ok) {
      setErrorMessage(check.msg);
      return;
    }
    const updated = [...connectors];
    const defaults = getConnectorDefaults(value, chargerType);
    const isSingleCcsType = ['ccs', 'ccs2'].includes(String(value).toLowerCase());
    updated[idx] = {
      ...updated[idx],
      type: value,
      maxPower: clampConnectorPower(defaults.power, value),
      maxCurrent: defaults.current,
      ...(isSingleCcsType ? { side: updated[idx].side || 'left' } : { side: undefined })
    };
    setConnectors(updated);
  };

  const handleConnectorFieldChange = (idx, key, value) => {
    const updated = [...connectors];
    const isAC = String(updated[idx]?.type || '').toLowerCase() === AC_TYPE;
    if (key === 'maxPower') {
      updated[idx].maxPower = clampConnectorPower(value, updated[idx]?.type);
    } else if (key === 'maxCurrent') {
      if (!isAC) {
        updated[idx].maxCurrent = Number(value);
      }
    } else {
      updated[idx][key] = value;
    }
    setConnectors(updated);
  };

  const handleAddSatellite = () => {
    if (isAxonEasy) {
      setErrorMessage(t('errorAxonEasyUsesConnectors'));
      return;
    }
    const defaultSat = configData.satellites[0];
    const newSat = {
      type: defaultSat.value,
      outputs: getDefaultOutputs(defaultSat.value),
      maxCurrent: 500,
      ...(chargerType === 'axon_side_dlbs_720' ? { maxCurrentDLBS1: 500, maxCurrentDLBS2: 500 } : {}),
      ...(chargerType === 'axon_side_dlbs_v2_960' ? { maxCurrentDLBS1: 500, maxCurrentDLBS2: 500 } : {}),
      ...(chargerType === 'axon_side_dlbs_v2_1440' ? { maxCurrentDLBS1: 500, maxCurrentDLBS2: 500, maxCurrentDLBS3: 500 } : {}),
      meter: configData.meters[0].value,
      terminal: configData.terminals[0].value
    };
    if (defaultSat.value === 'sat600') {
      newSat.led = 'led3';
    }
    const total = satellites.reduce((sum, s) => sum + s.outputs, 0) + newSat.outputs;
    if (total > 6) {
      setErrorMessage(t('errorMaxSixOutputs'));
      return;
    }
    setSatellites([...satellites, newSat]);
  };

  const handleRemoveSatellite = (idx) => {
    setSatellites(satellites.filter((_, i) => i !== idx));
  };

  const handleSatelliteChange = (idx, key, value) => {
    if (isAxonEasy) {
      setErrorMessage(t('errorAxonEasyUsesConnectors'));
      return;
    }
    const updated = [...satellites];
    if (key === 'type') {
      const newOut = getDefaultOutputs(value);
      const totalOther = updated.reduce((sum, s, i) => sum + (i === idx ? 0 : s.outputs), 0);
      if (totalOther + newOut > 6) {
        setErrorMessage(t('errorMaxSixOutputs'));
        return;
      }
      updated[idx].type = value;
      updated[idx].outputs = newOut;
      if (value === 'sat600') {
        updated[idx].led = updated[idx].led || 'led3';
      } else {
        delete updated[idx].led;
      }
      if (['satbox', 'satone'].includes(value)) {
        updated[idx].terminal = 'none';
      }
    } else {
      updated[idx][key] = ['maxCurrent', 'maxCurrentDLBS1', 'maxCurrentDLBS2', 'maxCurrentDLBS3'].includes(key) ? Number(value) : value;
    }
    setSatellites(updated);
  };

  const generateConfig = () => {
    const config = {
      ...(lekoVersion ? { lekoVersion } : {}),
      main: {
        chargerType,
        maxPower: Number(maxPower),
        currency,
        ...(lekoVersion ? { lekoVersion } : {}),
        templateName: templateName.trim(),
        ...(templateDescription.trim() ? { description: templateDescription.trim() } : {}),
        eichrecht: enableEichrecht,
        ...(isAxonEasy ? { meter: mainMeter, terminal: mainTerminal } : {}),
        ...(isAxonEasyDLBS ? { fireExtinguisher: hasFireExtinguisher, coolingSystem: hasCoolingSystem } : {}),
        ...(isAxonEasyLegacy ? { fireExtinguisher: hasFireExtinguisher } : {})
      },
      ...(isAxonEasy
        ? {
            connectors: connectors.map(c => ({
              type: c.type,
              maxPower: clampConnectorPower(c.maxPower, c.type),
              maxCurrent: Number(c.maxCurrent) || 0,
              ...(shouldShowSingleCcsSide &&
              ['ccs', 'ccs2'].includes(String(c.type || '').toLowerCase())
                ? { side: c.side || 'left' }
                : {})
            }))
          }
        : {
            satellites: satellites.map(s => ({
              type: s.type,
              outputs: getDefaultOutputs(s.type),
              maxCurrent: s.maxCurrent,
              ...(chargerType === 'axon_side_dlbs_720' ? { maxCurrentDLBS1: s.maxCurrentDLBS1, maxCurrentDLBS2: s.maxCurrentDLBS2 } : {}),
              ...(chargerType === 'axon_side_dlbs_v2_960' ? { maxCurrentDLBS1: s.maxCurrentDLBS1, maxCurrentDLBS2: s.maxCurrentDLBS2 } : {}),
              ...(chargerType === 'axon_side_dlbs_v2_1440' ? { maxCurrentDLBS1: s.maxCurrentDLBS1, maxCurrentDLBS2: s.maxCurrentDLBS2, maxCurrentDLBS3: s.maxCurrentDLBS3 } : {}),
              meter: s.meter,
              terminal: ['satbox', 'satone'].includes(s.type) ? undefined : s.terminal,
              ...(s.type === 'sat600' ? { led: s.led || 'led3' } : {})
            }))
          })
    };
    return JSON.stringify(config, null, 2);
  };

  const authorizedHeaders = async (headers = {}) => ({
    ...headers,
    ...(await getAuthHeaders(auth))
  });

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const parseJsonResponse = async (response) => {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch (_) {
      return {
        error: response.ok
          ? text
          : `${response.status} ${response.statusText || t('errorBackendCommunication')}`
      };
    }
  };

  const getAuditState = () => ({
    chargerType,
    maxPower,
    currency,
    lekoVersion,
    templateName,
    satelliteCount: satellites.length,
    satellites,
    connectorCount: connectors.length,
    connectors,
    enableFactoryReset,
    enableSerialization,
    enableEichrecht,
    hasFireExtinguisher,
    hasCoolingSystem,
    mainMeter,
    mainTerminal,
    language,
    lastConfigId,
    lastZipName
  });

  const auditEvent = async (event, details = {}) => {
    try {
      await fetch(`${BACKEND_URL}/api/audit`, {
        method: 'POST',
        headers: await authorizedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          event,
          timestamp: new Date().toISOString(),
          eventId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          url: window.location.href,
          details,
          appState: getAuditState()
        })
      });
    } catch (_) {
      // Audit logging must never block the user workflow.
    }
  };

  const handleFeedback = () => {
    setFeedbackMessage('');
    setFeedbackModalOpen(true);
  };

  const closeFeedbackModal = () => {
    if (feedbackSending) return;
    setFeedbackModalOpen(false);
    setFeedbackMessage('');
  };

  const submitFeedback = async () => {
    const feedback = feedbackMessage.trim();
    if (!feedback) return;

    const submittedAt = new Date().toISOString();
    const appState = getAuditState();

    try {
      setFeedbackSending(true);
      const response = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: await authorizedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          message: feedback,
          submittedAt,
          username: getCurrentUsername(),
          templateName: templateName.trim(),
          frontendVersion: FRONTEND_VERSION,
          backendVersion,
          url: window.location.href,
          userAgent: navigator.userAgent,
          appState
        })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || t('feedbackError'));
      }
      setFeedbackModalOpen(false);
      setFeedbackMessage('');
      setInfoMessage(t('feedbackSent'));
    } catch (error) {
      setErrorMessage(error.message || t('feedbackError'));
    } finally {
      setFeedbackSending(false);
    }
  };

  const downloadBlob = async (url, filename) => {
    const response = await fetch(url, {
      headers: await authorizedHeaders()
    });
    if (!response.ok) {
      throw new Error(t('errorDownload'));
    }

    const blob = await response.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const downloadArchive = async (configId, zipName) => {
    const fileUrl = `${BACKEND_URL}/api/download/${zipName}`;
    try {
      await downloadBlob(fileUrl, zipName || `${configId}.zip`);
      auditEvent('download_config_finished', { configId, zipName });
    } catch (error) {
      auditEvent('download_config_failed', { configId, zipName, message: error.message });
      setErrorMessage(error.message);
    }
  };

  const commitToGit = async (configId, configName) => {
    setCommitAttempted(true);
    setLoadingCommit(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/commit`, {
        method: 'POST',
        headers: await authorizedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ configId, configName })
      });
      const response = await r.json();
      const status = response.status;
      const message = response.message || response.error || '';
      if (status === 'committed') {
        auditEvent('commit_config_finished', { configId, configName, status });
        setInfoMessage(t('infoSentToGitLab'));
        setTemplateAlreadyExists(false);
      } else if (status === 'exists') {
        auditEvent('commit_config_exists', { configId, configName, status, message });
        setInfoMessage(`${t('infoTemplateAlreadyExists')} ${templateName}.zip`);
        setTemplateAlreadyExists(true);
      } else {
        auditEvent('commit_config_failed', { configId, configName, status, message });
        setErrorMessage(message || t('errorCommit'));
      }
    } catch (error) {
      auditEvent('commit_config_failed', { configId, configName, message: error.message });
      setErrorMessage(error.message || t('errorCommit'));
    } finally {
      setLoadingCommit(false);
    }
  };

  const sendToBackend = async () => {
    if (!templateName.trim()) {
      setErrorMessage(t('errorTemplateNameRequired'));
      return;
    }
    if (!isAxonEasy && satellites.length === 0) {
      setErrorMessage(t('errorAddAtLeastOneSatellite'));
      return;
    }
    if (isAxonEasy && connectors.length === 0) {
      setErrorMessage(t('errorAddAtLeastOneConnector'));
      return;
    }
    if (isAxonEasyDLBS && ccsConnectors.length < 2) {
      setErrorMessage(t('errorAxonEasyDlbsNeedsTwoCcs'));
      return;
    }

    const chargerDefaultPower = getChargerDefaultPower(chargerType);
    if ((Number(maxPower) || 0) > chargerDefaultPower) {
      setErrorMessage(`${t('errorMaxPowerCannotExceed')} ${chargerDefaultPower} kW`);
      setMaxPower(String(chargerDefaultPower));
      return;
    }

    if (isAxonEasy) {
      const stationMaxPower = Number(maxPower) || 0;
      const invalidConnector = connectors.find(conn =>
        String(conn.type || '').toLowerCase() !== AC_TYPE &&
        (Number(conn.maxPower) || 0) > stationMaxPower
      );

      if (invalidConnector) {
        setErrorMessage(t('errorConnectorPowerCannotExceedStationPower'));
        setConnectors(prev => prev.map(conn => ({
          ...conn,
          maxPower: clampConnectorPower(conn.maxPower, conn.type)
        })));
        return;
      }
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/configure`, {
        method: 'POST',
        headers: await authorizedHeaders({ 'Content-Type': 'application/json' }),
        body: generateConfig()
      });
      const res = await response.json();
      if (res.status === 'success') {
        setInfoMessage(t('infoConfigSent'));
        const zipPath = res.archive;
        const zipName = zipPath.split('/').pop();
        const configId = res.configId || zipName.replace('.zip', '');
        setLastConfigId(configId);
        setLastZipName(zipName);
        auditEvent('generate_config_finished', { configId, zipName, archive: zipPath });
        if (ENABLE_DOWNLOAD) {
          downloadArchive(zipName, zipName);
        }
      } else {
        auditEvent('generate_config_failed', { message: res.error || t('errorSending') });
        setErrorMessage(res.error || t('errorSending'));
      }
    } catch (error) {
      auditEvent('generate_config_failed', { message: error.message });
      setErrorMessage(error.message || t('errorBackendCommunication'));
    }
  };

  const handleDownloadFromMegatool = async () => {
    if (!templateName.trim()) {
      setErrorMessage(t('errorTemplateNameRequired'));
      return;
    }
    try {
      setLoadingSwu(true);
      setLoadingSwuStatus(t('megatoolStageStarting'));
      const branch = DEFAULT_BRANCH;
      const mtInitUrl = `${BACKEND_URL}/api/mt-download/${templateName}?branch=${branch}`;

      const payload = {
        factoryReset: enableFactoryReset,
        ...(lekoVersion ? { lekoVersion } : {})
      };

      if (enableSerialization) {
        payload.serialization = {
          main: serializationMain,
          sats: serializationSats
        };
      }

      const res = await fetch(mtInitUrl, {
        method: 'POST',
        headers: await authorizedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const result = await parseJsonResponse(res);
      if (!res.ok) throw new Error(result.error || t('errorDownload'));

      const statusUrl = `${BACKEND_URL}/api/mt-download/${templateName}/status`;
      let job = result;
      for (let attempt = 0; attempt < 1440; attempt += 1) {
        if (job.status === 'ready') {
          break;
        }
        if (job.status === 'error') {
          throw new Error(job.error || t('errorDownload'));
        }

        const stageKey = job.stage ? `megatoolStage_${job.stage}` : 'megatoolStagePreparing';
        let nextStatus = t(stageKey);
        if (job.stage === 'downloading_package' && job.downloadedBytes && job.sizeBytes) {
          nextStatus = `${nextStatus} (${Math.round((job.downloadedBytes / job.sizeBytes) * 100)}%)`;
        }
        setLoadingSwuStatus(nextStatus);
        await sleep(5000);

        const statusResponse = await fetch(statusUrl, {
          headers: await authorizedHeaders()
        });
        job = await parseJsonResponse(statusResponse);
        if (!statusResponse.ok) {
          throw new Error(job.error || t('errorDownload'));
        }
      }

      if (job.status !== 'ready') {
        throw new Error(t('errorMegatoolTimeout'));
      }

      setLoadingSwuStatus(t('megatoolStageDownloading'));
      await downloadBlob(`${BACKEND_URL}/api/get-swu/${templateName}`, `${templateName}.zip`);

      auditEvent('megatool_download_finished', { templateName, branch, filename: `${templateName}.zip` });
      setInfoMessage(t('infoSwuDownloaded'));
      setLoadingSwu(false);
      setLoadingSwuStatus('');
    } catch (err) {
      auditEvent('megatool_download_failed', { templateName, message: err.message });
      setErrorMessage(err.message);
      setLoadingSwu(false);
      setLoadingSwuStatus('');
    }
  };

  if (auth.loading) {
    return (
      <div className="App auth-screen">
        <div className="auth-panel">{t('loading')}</div>
      </div>
    );
  }

  if (auth.error) {
    return (
      <div className="App auth-screen">
        <div className="auth-panel auth-panel-error">
          <p>{auth.error}</p>
          <button onClick={() => window.location.reload()} type="button">
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="top-bar">
        <h1>{t('appTitle')}</h1>

        <div className="top-actions">
          {auth.enabled && (
            <button
              className="top-action-button"
              onClick={() => auth.client.logout()}
              type="button"
              title={auth.client.tokenParsed?.preferred_username || ''}
            >
              Logout
            </button>
          )}
          <button
            className="top-action-button"
            onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
            type="button"
          >
            {language === 'pl' ? 'PL' : 'EN'}
          </button>
        </div>
      </div>
      <div className="horizontal-layout">
        {/* Ładowarka po lewej */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
          <div style={{ textAlign: 'center', color: 'rgba(120,120,120,0.6)', fontSize: '22px', marginBottom: '10px', fontWeight: 600, width: '100%' }}>
            {t('selectChargerModel')}
          </div>
          <div className="charger-tile" style={{ width: '300px', boxSizing: 'border-box' }}>
            <img src={getChargerImage(chargerType)} alt="charger" className="device-image" />
            <div className="form-column">
              <label>
                <span>{t('chargerType')}</span>
              </label>
              <select
                value={chargerType}
                onChange={e => {
                  const v = e.target.value;
                  setChargerType(v);
                  const defP = getChargerDefaultPower(v);
                  setMaxPower(String(defP));

                  if (String(v || '').toLowerCase().startsWith('axon_easy')) {
                    setConnectors(prevConnectors => prevConnectors.map(conn => {
                      const defaults = getConnectorDefaults(conn.type, v);
                      const isAC = String(conn.type || '').toLowerCase() === AC_TYPE;
                      return {
                        ...conn,
                        maxPower: isAC ? defaults.power : clampConnectorPower(defaults.power, conn.type, defP),
                        maxCurrent: isAC ? 0 : defaults.current
                      };
                    }));
                  }
                  const easy = String(v || '').toLowerCase().startsWith('axon_easy');
                  if (easy) {
                    if (satellites.length) setSatellites([]);
                    if (String(mainMeter).toLowerCase() === 'none' && axonEasyMeters.length) {
                      setMainMeter(axonEasyMeters[0].value);
                    }
                  } else {
                    if (connectors.length) setConnectors([]);
                  }
                }}
              >
                {configData.chargers.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <label>
                <span>{t('maxPowerKw')}</span>
              </label>
              <input
                type="number"
                max={getChargerDefaultPower(chargerType)}
                value={maxPower}
                onChange={e => {
                  const nextPower = String(clampChargerPower(e.target.value));
                  setMaxPower(nextPower);

                  if (isAxonEasy) {
                    setConnectors(prevConnectors =>
                      prevConnectors.map(conn => ({
                        ...conn,
                        maxPower: clampConnectorPower(conn.maxPower, conn.type, nextPower)
                      }))
                    );
                  }
                }}
              />
              {isAxonEasy && (
                <>
                  <label>
                    <span>{t('meter')}</span>
                  </label>
                  <select value={mainMeter} onChange={e => setMainMeter(e.target.value)}>
                    {axonEasyMeters.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <label>
                    <span>{t('terminal')}</span>
                  </label>
                  <select value={mainTerminal} onChange={e => setMainTerminal(e.target.value)}>
                    {configData.terminals.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </>
              )}

              <label>
                <span>{t('currency')}</span>
              </label>
              <select value={currency} onChange={e => setCurrency(e.target.value)}>
                {configData.currencies.map(cur => (
                  <option key={cur.value} value={cur.value}>{cur.label}</option>
                ))}
              </select>
              <label>
                <span>{t('lekoVersion')}</span>
              </label>
              <select value={lekoVersion} onChange={e => setLekoVersion(e.target.value)}>
                {(configData.lekoVersions || []).map(version => (
                  <option key={version.value} value={version.value}>{version.label}</option>
                ))}
              </select>
              <label>
                <span>{t('templateName')}</span>
              </label>
              <input
                type="text"
                placeholder="3-21-52.0134"
                value={templateName}
                onBeforeInput={e => {
                  if (e.data && TEMPLATE_NAME_DISALLOWED_CHAR_RE.test(e.data)) {
                    e.preventDefault();
                  }
                }}
                onChange={e => setTemplateName(sanitizeTemplateName(e.target.value))}
              />
              <label>
                <span>{t('description')}</span>
              </label>
              <input
                type="text"
                placeholder="max limit 110kW, limit AC to 11kW, CCS2/AC alternately"
                value={templateDescription}
                onChange={e => setTemplateDescription(sanitizeDescription(e.target.value))}
              />

              <div
                style={{
                  height: '1px',
                  width: '100%',
                  background: 'rgba(142, 142, 142, 0.31)',
                  margin: '14px 0 12px 0'
                }}
              />

              {isAxonEasyDLBS && (
                <div className="checkbox-group">
                  <label className="checkbox-row">
                    <span>{t('fireExtinguisher')}</span>
                    <input
                      type="checkbox"
                      checked={hasFireExtinguisher}
                      onChange={() => setHasFireExtinguisher(!hasFireExtinguisher)}
                    />
                  </label>
                  <label className="checkbox-row">
                    <span>{t('coolingSystem')}</span>
                    <input
                      type="checkbox"
                      checked={hasCoolingSystem}
                      onChange={() => setHasCoolingSystem(!hasCoolingSystem)}
                    />
                  </label>
                </div>
              )}

              {isAxonEasy && !isAxonEasyDLBS && (
                <div className="checkbox-group">
                  <label className="checkbox-row">
                    <span>{t('fireExtinguisher')}</span>
                    <input
                      type="checkbox"
                      checked={hasFireExtinguisher}
                      onChange={() => setHasFireExtinguisher(!hasFireExtinguisher)}
                    />
                  </label>
                </div>
              )}

              <div className="checkbox-group">
                <label className="checkbox-row">
                  <span>{t('eichrecht')}</span>
                  <input
                    type="checkbox"
                    checked={enableEichrecht}
                    onChange={() => setEnableEichrecht(!enableEichrecht)}
                  />
                </label>
                <label className="checkbox-row">
                  <span>{t('factoryReset')}</span>
                  <input
                    type="checkbox"
                    checked={enableFactoryReset}
                    onChange={() => setEnableFactoryReset(!enableFactoryReset)}
                  />
                </label>
                <label>
                  <span>{t('serialization')}</span>
                  <input
                    type="checkbox"
                    checked={enableSerialization}
                    onChange={() => setEnableSerialization(!enableSerialization)}
                  />
                </label>
              </div>

              {enableSerialization && (
                <div className="serialization-fields">
                  <label>{t('model')}</label>
                  <input placeholder="Model" value={serializationMain.model} onChange={e => setSerializationMain({ ...serializationMain, model: e.target.value })} />
                  <label>{t('product')}</label>
                  <input placeholder="Product" value={serializationMain.product} onChange={e => setSerializationMain({ ...serializationMain, product: e.target.value })} />
                  <label>{t('serial')}</label>
                  <input placeholder="Serial" value={serializationMain.serial} onChange={e => setSerializationMain({ ...serializationMain, serial: e.target.value })} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Satelity / Złącza po prawej */}
        {!isAxonEasy ? (
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            <div style={{ textAlign: 'left', color: 'rgba(120,120,120,0.6)', fontSize: '22px', marginBottom: '10px', fontWeight: 600 }}>
              {t('addSatellites')}
            </div>
            <div className="satellite-scroll">
              {satellites.map((sat, i) => (
                <div key={i} className="satellite-tile">
                  <button className="remove-btn" onClick={() => handleRemoveSatellite(i)}>&times;</button>
                  <img src={getSatelliteImage(sat.type)} alt="satellite" className="device-image" />
                  <div className="form-column">
                    <label>
                      <span>{t('satelliteType')}</span>
                    </label>
                    <select value={sat.type} onChange={e => handleSatelliteChange(i, 'type', e.target.value)}>
                      {configData.satellites.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    <label>
                      <span>{t('maxCurrentA')}</span>
                    </label>
                    <input type="number" value={sat.maxCurrent} onChange={e => handleSatelliteChange(i, 'maxCurrent', e.target.value)} />
                    {chargerType === 'axon_side_dlbs_720' && (
                      <>
                        <label>
                          <span>{t('maxCurrentSide1A')}</span>
                        </label>
                        <input
                          type="number"
                          value={sat.maxCurrentDLBS1 ?? ''}
                          onChange={e => handleSatelliteChange(i, 'maxCurrentDLBS1', e.target.value)}
                        />
                        <label>
                          <span>{t('maxCurrentSide2A')}</span>
                        </label>
                        <input
                          type="number"
                          value={sat.maxCurrentDLBS2 ?? ''}
                          onChange={e => handleSatelliteChange(i, 'maxCurrentDLBS2', e.target.value)}
                        />
                      </>
                    )}
                    {chargerType === 'axon_side_dlbs_v2_960' && (
                      <>
                        <label>
                          <span>{t('maxCurrentSide1A')}</span>
                        </label>
                        <input
                          type="number"
                          value={sat.maxCurrentDLBS1 ?? ''}
                          onChange={e => handleSatelliteChange(i, 'maxCurrentDLBS1', e.target.value)}
                        />
                        <label>
                          <span>{t('maxCurrentSide2A')}</span>
                        </label>
                        <input
                          type="number"
                          value={sat.maxCurrentDLBS2 ?? ''}
                          onChange={e => handleSatelliteChange(i, 'maxCurrentDLBS2', e.target.value)}
                        />
                      </>
                    )}
                    {chargerType === 'axon_side_dlbs_v2_1440' && (
                      <>
                        <label>
                          <span>{t('maxCurrentSide1A')}</span>
                        </label>
                        <input
                          type="number"
                          value={sat.maxCurrentDLBS1 ?? ''}
                          onChange={e => handleSatelliteChange(i, 'maxCurrentDLBS1', e.target.value)}
                        />
                        <label>
                          <span>{t('maxCurrentSide2A')}</span>
                        </label>
                        <input
                          type="number"
                          value={sat.maxCurrentDLBS2 ?? ''}
                          onChange={e => handleSatelliteChange(i, 'maxCurrentDLBS2', e.target.value)}
                        />
                        <label>
                          <span>{t('maxCurrentSide3A')}</span>
                        </label>
                        <input
                          type="number"
                          value={sat.maxCurrentDLBS3 ?? ''}
                          onChange={e => handleSatelliteChange(i, 'maxCurrentDLBS3', e.target.value)}
                        />
                      </>
                    )}
                    <label>
                      <span>{t('meter')}</span>
                    </label>
                    <select value={sat.meter} onChange={e => handleSatelliteChange(i, 'meter', e.target.value)}>
                      {configData.meters.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                    {sat.type === 'sat600' && (
                      <>
                        <label>
                          <span>{t('ledController')}</span>
                        </label>
                        <select
                          value={sat.led || 'led3'}
                          onChange={e => handleSatelliteChange(i, 'led', e.target.value)}
                        >
                          <option value="led3">LED3</option>
                          <option value="led1">LED1</option>
                        </select>
                      </>
                    )}
                    {!['satbox', 'satone'].includes(sat.type) && (
                      <>
                        <label>
                          <span>{t('terminalType')}</span>
                        </label>
                        <select value={sat.terminal} onChange={e => handleSatelliteChange(i, 'terminal', e.target.value)}>
                          {configData.terminals.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      </>
                    )}
                    {enableSerialization && (
                      <div className="serialization-fields">
                        <label>{t('model')}</label>
                        <input
                          placeholder="Model"
                          value={serializationSats[i]?.model || ''}
                          onChange={e => setSerializationSats({ ...serializationSats, [i]: { ...serializationSats[i], model: e.target.value } })}
                        />
                        <label>{t('product')}</label>
                        <input
                          placeholder="Product"
                          value={serializationSats[i]?.product || ''}
                          onChange={e => setSerializationSats({ ...serializationSats, [i]: { ...serializationSats[i], product: e.target.value } })}
                        />
                        <label>{t('serial')}</label>
                        <input
                          placeholder="Serial"
                          value={serializationSats[i]?.serial || ''}
                          onChange={e => setSerializationSats({ ...serializationSats, [i]: { ...serializationSats[i], serial: e.target.value } })}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="add-remove-buttons">
                <button onClick={handleAddSatellite}>+</button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            <div style={{ textAlign: 'left', color: 'rgba(120,120,120,0.6)', fontSize: '22px', marginBottom: '10px', fontWeight: 600 }}>
              {isAxonEasyDLBS ? t('addConnectorsMinTwoCcs') : t('addConnectors')}
            </div>
            <div className="satellite-scroll">
              {connectors.map((conn, i) => (
                <div key={i} className="satellite-tile">
                  <button className="remove-btn" onClick={() => handleRemoveConnector(i)}>&times;</button>
                  <img src={getConnectorImage(conn.type)} alt="connector" className="device-image" />
                  <div className="form-column">
                    <label>
                      <span>{t('connectorType')}</span>
                    </label>
                    <select value={conn.type} onChange={e => handleConnectorChange(i, e.target.value)}>
                      {(configData.connectors || [
                        { value: 'ccs', label: 'CCS' },
                        { value: 'ccs2', label: 'CCS2' },
                        { value: 'nacs', label: 'NACS' },
                        { value: 'ac', label: 'AC' }
                      ]).map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>

                    {shouldShowSingleCcsSide && ['ccs', 'ccs2'].includes(String(conn.type || '').toLowerCase()) && (
                      <>
                        <label>
                          <span>{t('ccsConnectorPosition')}</span>
                        </label>
                        <select
                          value={conn.side || 'left'}
                          onChange={e => handleConnectorFieldChange(i, 'side', e.target.value)}
                        >
                          <option value="left">{t('left')}</option>
                          <option value="right">{t('right')}</option>
                        </select>
                      </>
                    )}

                    <label>
                      <span>{t('connectorMaxPowerKw')}</span>
                    </label>
                    <input
                      type="number"
                      max={String(conn.type).toLowerCase() === AC_TYPE ? 44 : (Number(maxPower) || 0)}
                      value={conn.maxPower ?? 0}
                      onChange={e => handleConnectorFieldChange(i, 'maxPower', e.target.value)}
                    />
                    {String(conn.type).toLowerCase() !== AC_TYPE && (
                      <>
                        <label>
                          <span>{t('connectorMaxCurrentA')}</span>
                        </label>
                        <input
                          type="number"
                          value={conn.maxCurrent ?? 0}
                          onChange={e => handleConnectorFieldChange(i, 'maxCurrent', e.target.value)}
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}

              <div className="add-remove-buttons">
                <button onClick={handleAddConnector}>+</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="popup-error-overlay" onClick={() => setErrorMessage('')}>
          <div className="popup-error" onClick={(e) => e.stopPropagation()}>
            <p>{errorMessage}</p>
            <button onClick={() => setErrorMessage('')}>OK</button>
          </div>
        </div>
      )}

      {infoMessage && (
        <div className="popup-info-overlay" onClick={() => setInfoMessage('')}>
          <div className="popup-info" onClick={(e) => e.stopPropagation()}>
            <p>{infoMessage}</p>
            <button onClick={() => setInfoMessage('')}>OK</button>
          </div>
        </div>
      )}

      {loadingSwu && (
        <div className="popup-info-overlay">
          <div className="popup-info" style={{ textAlign: 'center' }}>
            <p>⏳ {loadingSwuStatus || t('loadingSwuFromMegatool')}</p>
          </div>
        </div>
      )}

      {loadingCommit && (
        <div className="popup-info-overlay">
          <div className="popup-info" style={{ textAlign: 'center' }}>
            <p>⏳ {t('loadingCommitToGitLab')}</p>
          </div>
        </div>
      )}

      {feedbackModalOpen && (
        <div className="modal-overlay" onClick={closeFeedbackModal}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <div>
                <h2>{t('feedbackTitle')}</h2>
                <p>{t('feedbackSubtitle')}</p>
              </div>
              <button
                className="modal-close-button"
                onClick={closeFeedbackModal}
                disabled={feedbackSending}
                type="button"
                aria-label={t('close')}
              >
                &times;
              </button>
            </div>
            <div className="feedback-context">
              <div className="feedback-context-item">
                <span>{t('feedbackUser')}</span>
                <b>{getCurrentUsername()}</b>
              </div>
              <div className="feedback-context-item">
                <span>{t('feedbackTemplate')}</span>
                <b>{templateName.trim() || t('missingName')}</b>
              </div>
              <div className="feedback-context-item">
                <span>{t('feedbackCharger')}</span>
                <b>{chargerType}</b>
              </div>
            </div>
            <div className="feedback-field">
              <label className="feedback-textarea-label" htmlFor="feedback-message">
                {t('feedbackPrompt')}
              </label>
              <textarea
                id="feedback-message"
                className="feedback-textarea"
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                maxLength={10000}
                autoFocus
              />
              <div className="feedback-helper">
                <span>{t('feedbackContextHint')}</span>
                <span>{feedbackMessage.length}/10000</span>
              </div>
            </div>
            <div className="feedback-modal-actions">
              <button
                className="secondary-button"
                onClick={closeFeedbackModal}
                disabled={feedbackSending}
                type="button"
              >
                {t('cancel')}
              </button>
              <button
                className="primary-button"
                onClick={submitFeedback}
                disabled={feedbackSending || !feedbackMessage.trim()}
                type="button"
              >
                {feedbackSending ? t('feedbackSending') : t('submitFeedback')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bottom-controls">
        <button onClick={sendToBackend}>📤 {t('generate')}</button>

        {lastZipName && (
          <button onClick={() => downloadArchive(lastConfigId, lastZipName)}>📦 {t('downloadConfig')}</button>
        )}
        {lastConfigId && (
          <>
            <button onClick={() => commitToGit(lastConfigId, templateName.trim())}>📝 {t('commitToGitLab')}</button>
          </>
        )}
        {lastConfigId && (
          <div className="existing-template-info">
            <button
              onClick={handleDownloadFromMegatool}
              disabled={!lastConfigId || !lastZipName}
            >
              ⬇️ {t('downloadFromMegatool')}
            </button>
          </div>
        )}
      </div>
      <div className="commit-info">
        {lastConfigId && (
          <>
            <p>{t('fileWillBeSavedAsTemplate')} <code>{lastZipName || `${lastConfigId}.zip`}</code> → <code>{templateName || t('missingName')}</code>.</p>
            <p><b>{t('fileWillBeDeletedInTenMinutes')}</b></p>
            {templateAlreadyExists && (
              <>
                <p>📦 {t('templateAlreadyExistsInRepo')} <code>{templateName}.swu</code>.</p>
                <p>{t('downloadExistingSwuHint')}</p>
              </>
            )}
          </>
        )}
      </div>
      <footer className="app-footer">
        <div className="app-version-info">
          <span>{t('frontendVersion')}: <b>{FRONTEND_VERSION}</b></span>
          <span>{t('backendVersion')}: <b>{backendVersion}</b></span>
        </div>
        <button className="feedback-button" onClick={handleFeedback} type="button">
          {t('sendFeedback')}
        </button>
      </footer>
    </div>
  );
}

export default App;
